import {
  CheckCircleIcon,
  WarningIcon,
  QuestionIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  InventoryStatus,
  type InventoryValidationResult,
} from '@erp/shared-types';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';

interface InventoryBadgeProps {
  inventoryValidation?: InventoryValidationResult;
  /** Show loading spinner while inventory is being checked */
  isLoading?: boolean;
}

function formatQuantity(value: number | null | undefined): string {
  if (value == null) {
    return '-';
  }
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function getInventoryBadgeContent(status: InventoryStatus) {
  switch (status) {
    case InventoryStatus.SUFFICIENT:
      return {
        icon: <CheckCircleIcon size={14} className="text-green-600" />,
        label: 'In Stock',
        color: '#2e7d32',
        bgcolor: 'rgba(46, 125, 50, 0.1)',
      };
    case InventoryStatus.INSUFFICIENT:
      return {
        icon: <WarningIcon size={14} className="text-red-600" />,
        label: 'Low Stock',
        color: '#d32f2f',
        bgcolor: 'rgba(211, 47, 47, 0.1)',
      };
    case InventoryStatus.UNKNOWN:
    default:
      return {
        icon: <QuestionIcon size={14} className="text-gray-500" />,
        label: 'Unknown',
        color: 'text.secondary',
        bgcolor: 'rgba(0, 0, 0, 0.08)',
      };
  }
}

function getTooltipContent(
  validation: InventoryValidationResult,
): React.ReactNode {
  const {
    status,
    requestedQuantity,
    availableQuantity,
    onhandQuantity,
    reservedQuantity,
    itemNumber,
    organizationCode,
    errorMessage,
  } = validation;

  if (status === InventoryStatus.UNKNOWN && errorMessage) {
    return (
      <div className="text-xs space-y-1">
        <div className="font-medium">Unable to check inventory</div>
        <div className="text-gray-300">{errorMessage}</div>
      </div>
    );
  }

  const shortage =
    availableQuantity !== null ? requestedQuantity - availableQuantity : null;

  return (
    <div className="text-xs space-y-1">
      <div className="font-medium">
        {status === InventoryStatus.SUFFICIENT
          ? 'Inventory Available'
          : status === InventoryStatus.INSUFFICIENT
            ? 'Insufficient Inventory'
            : 'Inventory Status Unknown'}
      </div>

      <div className="space-y-0.5 text-gray-300">
        <div className="flex justify-between gap-4">
          <span>Requested:</span>
          <span className="font-medium text-white">
            {formatQuantity(requestedQuantity)}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span>Available:</span>
          <span
            className={`font-medium ${status === InventoryStatus.INSUFFICIENT ? 'text-red-400' : 'text-white'}`}
          >
            {formatQuantity(availableQuantity)}
          </span>
        </div>
        {status === InventoryStatus.INSUFFICIENT && shortage !== null && (
          <div className="flex justify-between gap-4 text-red-400">
            <span>Shortage:</span>
            <span className="font-medium">{formatQuantity(shortage)}</span>
          </div>
        )}
      </div>

      {(onhandQuantity !== null || reservedQuantity !== null) && (
        <div className="pt-1 border-t border-gray-600 space-y-0.5 text-gray-400">
          {onhandQuantity !== null && (
            <div className="flex justify-between gap-4">
              <span>On-hand:</span>
              <span>{formatQuantity(onhandQuantity)}</span>
            </div>
          )}
          {reservedQuantity !== null && (
            <div className="flex justify-between gap-4">
              <span>Reserved:</span>
              <span>{formatQuantity(reservedQuantity)}</span>
            </div>
          )}
        </div>
      )}

      {(itemNumber || organizationCode) && (
        <div className="pt-1 border-t border-gray-600 text-gray-400">
          {itemNumber && <div>Item: {itemNumber}</div>}
          {organizationCode && <div>Org: {organizationCode}</div>}
        </div>
      )}
    </div>
  );
}

/**
 * Badge element for displaying inventory status
 */
function BadgeElement({
  badgeContent,
  displayLabel,
}: {
  badgeContent: ReturnType<typeof getInventoryBadgeContent>;
  displayLabel: string;
}) {
  return (
    <MDBox
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      gap={0.5}
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: '4px',
        bgcolor: badgeContent.bgcolor,
        cursor: 'help',
        minWidth: '110px',
      }}
    >
      {badgeContent.icon}
      <MDTypography
        variant="caption"
        sx={{
          color: badgeContent.color,
          fontWeight: 500,
          fontSize: '0.75rem',
          whiteSpace: 'nowrap',
        }}
      >
        {displayLabel}
      </MDTypography>
    </MDBox>
  );
}

const NO_INVENTORY_BADGE_CONTENT = {
  icon: <QuestionIcon size={14} className="text-gray-500" />,
  label: '-',
  color: 'text.secondary',
  bgcolor: 'rgba(0, 0, 0, 0.08)',
};

const LOADING_BADGE_CONTENT = {
  icon: <CircleNotchIcon size={14} className="text-blue-500 animate-spin" />,
  label: 'Checking...',
  color: '#1976d2',
  bgcolor: 'rgba(25, 118, 210, 0.1)',
};

export function InventoryBadge({
  inventoryValidation,
  isLoading,
}: InventoryBadgeProps) {
  // Show loading state while inventory is being checked
  if (isLoading) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <BadgeElement
              badgeContent={LOADING_BADGE_CONTENT}
              displayLabel={LOADING_BADGE_CONTENT.label}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">Checking inventory availability...</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  // If no inventory validation data, show neutral badge
  if (!inventoryValidation) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>
            <BadgeElement
              badgeContent={NO_INVENTORY_BADGE_CONTENT}
              displayLabel={NO_INVENTORY_BADGE_CONTENT.label}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <span className="text-xs">Inventory not checked</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  const badgeContent = getInventoryBadgeContent(inventoryValidation.status);
  const tooltipContent = getTooltipContent(inventoryValidation);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div>
          <BadgeElement
            badgeContent={badgeContent}
            displayLabel={badgeContent.label}
          />
        </div>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
}

export default InventoryBadge;
